import { useCallback, useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import useLocalStorageState, {
  LocalStorageOptions,
} from "use-local-storage-state";
import { useOktaAuth } from "@okta/okta-react";

import { DEFAULT_GENERAL_CONFIG } from "pages/SuperAdmin/constants";

import { FilterSchemaItem } from "features/ui/Filters/types";
import { getPageKeyWithVersion } from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

import { DEFAULT_MILEAGE_UNIT } from "./constants";
import { useConfigContext } from "./contexts/ConfigContext";
import useAssetsSchema from "./schemas/assetsSchema";
import { useClaimsSchema } from "./schemas/claimsSchema";
import { useCustomRecordsSchema } from "./schemas/customRecordsSchema";
import { useInspectionsSchema } from "./schemas/inspectionsSchema";
import useRepairLaborSchema from "./schemas/repairLaborSchema";
import useRepairPartsSchema from "./schemas/repairPartsSchema";
import useRepairsSchema, { useRepairsSchemaV2 } from "./schemas/repairsSchema";
import useSensorReadingsSchema from "./schemas/sensorReadingsSchema";
import useSignalEventOccurrencesSchema from "./schemas/signalEventOccurrencesSchema";
import useSignalEventOccurrencesV2Schema from "./schemas/signalEventOccurrencesV2Schema";
import useVehiclesSchema from "./schemas/vehiclesSchema";
import {
  EventTypeEnum,
  EventTypeEnumCapitalized,
  EventTypeEnumCapitalizedType,
  JWT,
  MileageUnit,
} from "./types";

export const useDebounce = <T>(value: T, delay: number): T => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
};

// Keeps an input's text to itself while it is typed and passes it on once typing pauses or the input loses focus,
// for inputs inside a grid where every committed edit redraws far more than the input.
export const useBufferedText = (
  value: string,
  onCommit: (value: string) => void,
  delay: number
) => {
  const [text, setText] = useState(value);
  const [syncedValue, setSyncedValue] = useState(value);
  const [isPending, setIsPending] = useState(false);
  const pendingRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const valueRef = useRef(value);
  const onCommitRef = useRef(onCommit);

  useEffect(() => {
    onCommitRef.current = onCommit;
  });

  // A change from outside, such as a background refetch, replaces what is shown unless the user's own text is still
  // waiting to be committed: that text wins, so a refetch never wipes an edit in progress. A commit of our own lands
  // here too, as the text already on screen.
  if (value !== syncedValue) {
    setSyncedValue(value);
    if (!isPending) setText(value);
  }

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const flush = useCallback(() => {
    clearTimeout(timeoutRef.current);
    const pending = pendingRef.current;
    pendingRef.current = null;
    setIsPending(false);
    if (pending === null || pending === valueRef.current) return;

    onCommitRef.current(pending);
  }, []);

  const change = useCallback(
    (next: string) => {
      setText(next);
      setIsPending(true);
      pendingRef.current = next;
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(flush, delay);
    },
    [delay, flush]
  );

  useEffect(() => flush, [flush]);

  return { text, change, flush };
};

export const useEmailFromJWT = (): string => {
  const { oktaAuth } = useOktaAuth();
  const accessToken = oktaAuth.getAccessToken() || "";

  if (!accessToken) return "";

  const { sub: email }: JWT = jwtDecode(accessToken);

  return (email as string) || "";
};

export const useClaimsFiltersSchema = (): SchemaEntry[] =>
  // we currently take all cols from Claims table and filter out the ones that don't have a filter
  useClaimsSchema().schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );

export const useInspectionsFiltersSchema = (): SchemaEntry[] =>
  useInspectionsSchema().schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );

export const useRepairsFiltersSchema = (): SchemaEntry[] => {
  const repairsSchema = useRepairsSchema();

  return repairsSchema.schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );
};

const ALLOWED_REPAIR_ACCESSORS = [
  "workOrderID",
  "workOrderDate",
  "serviceCenterName",
];

export const useRepairPartsFiltersSchema = (): SchemaEntry[] =>
  useRepairPartsSchema([], ALLOWED_REPAIR_ACCESSORS).schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );

export const useRepairLaborFiltersSchema = (): SchemaEntry[] =>
  useRepairLaborSchema([], ALLOWED_REPAIR_ACCESSORS).schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );

export const useRepairsFiltersSchemaV2 = (): SchemaEntry[] =>
  useRepairsSchemaV2().schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );

export const useCustomRecordsFiltersSchema = (): SchemaEntry[] =>
  // we currently take all cols from Custom Records table and filter out the ones that don't have a filter
  useCustomRecordsSchema().schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );

interface UseFiltersSchemaProps {
  disableIsNotFilteredFilters?: boolean;
}

export const useVehiclesFiltersSchema = (
  options?: UseFiltersSchemaProps,
  accessorsToHide: string[] = []
): SchemaEntry[] => {
  const { disableIsNotFilteredFilters } = options || {};

  const { schema: vehicleAttributesSchema } =
    useVehiclesSchema(accessorsToHide);

  return vehicleAttributesSchema
    .filter(
      ({ filter, hideFilter }: SchemaEntry) => Boolean(filter) && !hideFilter
    )
    .map((schema) => ({
      ...schema,
      filter: {
        ...schema.filter,
        disableIsNotFilteredFilters,
      } as FilterSchemaItem,
    }));
};

export const useAssetsFiltersSchema = (
  options?: UseFiltersSchemaProps,
  accessorsToHide: string[] = []
): SchemaEntry[] => {
  const { disableIsNotFilteredFilters } = options || {};

  const { schema: vehicleAttributesSchema } = useAssetsSchema(accessorsToHide);

  return vehicleAttributesSchema
    .filter(
      ({ filter, hideFilter }: SchemaEntry) => Boolean(filter) && !hideFilter
    )
    .map((schema) => ({
      ...schema,
      filter: {
        ...schema.filter,
        disableIsNotFilteredFilters,
      } as FilterSchemaItem,
    }));
};

export const useSignalEventOccurrencesFiltersSchema = (
  accessorsToHide: string[] = []
): SchemaEntry[] => {
  const { schema } = useSignalEventOccurrencesSchema(accessorsToHide);

  return schema.filter(
    ({ filter, hideFilter }: SchemaEntry) => filter && !hideFilter
  );
};

export const useSignalEventOccurrencesV2FiltersSchema = (
  accessorsToHide: string[] = []
): SchemaEntry[] =>
  useSignalEventOccurrencesV2Schema(accessorsToHide).schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );

const useSensorReadingsFiltersSchema = (): SchemaEntry[] =>
  useSensorReadingsSchema().schema.filter(
    ({ filter, hideFilter }) => filter && !hideFilter
  );

export const useFilterSchemaForType = (
  type?: EventTypeEnum | EventTypeEnumCapitalizedType
): SchemaEntry[] | undefined => {
  const signalEventOccurrencesSchema = useSignalEventOccurrencesFiltersSchema();
  const claimsSchema = useClaimsFiltersSchema();
  const sensorReadingsSchema = useSensorReadingsFiltersSchema();
  const repairsSchema = useRepairsFiltersSchema();
  const inspectionsSchema = useInspectionsFiltersSchema();
  const customRecordsSchema = useCustomRecordsFiltersSchema();

  switch (type) {
    case EventTypeEnum.CLAIM:
    case EventTypeEnumCapitalized.CLAIM:
      return claimsSchema;
    case EventTypeEnum.SIGNAL_EVENT:
    case EventTypeEnumCapitalized.SIGNAL_EVENT:
      return signalEventOccurrencesSchema;
    case EventTypeEnum.SENSOR_READING:
    case EventTypeEnumCapitalized.SENSOR_READING:
      return sensorReadingsSchema;
    case EventTypeEnum.REPAIR:
    case EventTypeEnumCapitalized.REPAIR:
      return repairsSchema;
    case EventTypeEnum.INSPECTION:
    case EventTypeEnumCapitalized.INSPECTION:
      return inspectionsSchema;
    case EventTypeEnum.CUSTOM_RECORD:
    case EventTypeEnumCapitalized.CUSTOM_RECORD:
      return customRecordsSchema;
  }
};

/**
 * Just a custom wrapper around useLocalStorageState that adds a version suffix to the key
 * so we can bump all keys in our app at once.
 * Always use this instead of useLocalStorageState.
 */
export const useCustomLocalStorageState = <T>(
  key: string,
  options: LocalStorageOptions<T>
) =>
  useLocalStorageState<T>(getPageKeyWithVersion(key), {
    ...options,
    defaultValue: options.defaultValue ?? undefined,
  });

export const useTenantUnits = () => {
  const { general } = useConfigContext();

  return {
    distanceUnit: general?.distanceUnit ?? DEFAULT_GENERAL_CONFIG.distanceUnit,
    pressureUnit: general?.pressureUnit ?? DEFAULT_GENERAL_CONFIG.pressureUnit,
    temperatureUnit:
      general?.temperatureUnit ?? DEFAULT_GENERAL_CONFIG.temperatureUnit,
    timeUnit: general?.timeUnit ?? DEFAULT_GENERAL_CONFIG.timeUnit,
    voltageUnit: general?.voltageUnit ?? DEFAULT_GENERAL_CONFIG.voltageUnit,
    percentageUnit:
      general?.percentageUnit ?? DEFAULT_GENERAL_CONFIG.percentageUnit,
    frequencyUnit:
      general?.frequencyUnit ?? DEFAULT_GENERAL_CONFIG.frequencyUnit,
    currentUnit: general?.currentUnit ?? DEFAULT_GENERAL_CONFIG.currentUnit,
    energyUnit: general?.energyUnit ?? DEFAULT_GENERAL_CONFIG.energyUnit,
    angleUnit: general?.angleUnit ?? DEFAULT_GENERAL_CONFIG.angleUnit,
    speedUnit: general?.speedUnit ?? DEFAULT_GENERAL_CONFIG.speedUnit,
    currencyUnit: general?.currencyUnit ?? DEFAULT_GENERAL_CONFIG.currencyUnit,
  };
};

export const useTenantMileageUnit = (): MileageUnit => {
  const { general, loaded } = useConfigContext();

  // This should never be able to happen, so we raise an error
  if (!loaded)
    throw new Error("Can't get mileageUnit for tenant: config not loaded");

  return general?.mileageUnit ?? DEFAULT_MILEAGE_UNIT;
};

/**
 * Creates a key binding and listen for it until unmounted.
 *
 * @param {string[]} keys - Combination of keys to listen for.
 * Examples:  ["ctrl", "k"], ["ArrowUp"], ["ArrowDown", "Enter"]
 *
 * @param {string} callback - Function to be called when the key combination is pressed.
 * @param {boolean} capture -  catch the event during the capture phase.
 */
export const useKeyBinding = (
  keys: string[],
  callback: (...args: any[]) => void,
  capture: boolean = false
): void => {
  const handleNumberKey = (event: KeyboardEvent) => {
    const integer = parseInt(event.code[event.code.length - 1]);
    if (Number.isNaN(integer)) return event.key;

    return integer.toString();
  };

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      // Browsers can dispatch trusted keydown events without `code`/`key`
      // (e.g. autofill on text inputs); bail before reading `.length`/`.toLowerCase`.
      if (typeof event.code !== "string" || typeof event.key !== "string") {
        return;
      }

      const lowerCaseKeys = keys.map((key) => key.toLowerCase());
      const eventKey = handleNumberKey(event).toLowerCase();

      // Handle combination keys (cmd/ctrl + key)
      if (
        (lowerCaseKeys.includes("ctrl") || lowerCaseKeys.includes("cmd")) &&
        lowerCaseKeys.length > 1
      ) {
        if (
          (event.metaKey || event.ctrlKey) &&
          lowerCaseKeys.includes(eventKey)
        ) {
          event.preventDefault();
          callback();

          return;
        }
      }

      // Handle combination keys (alt/opt + key)
      if (
        (lowerCaseKeys.includes("alt") || lowerCaseKeys.includes("opt")) &&
        lowerCaseKeys.length > 1
      ) {
        // don't trigger on alt since the requirement is more than 1 key
        if (eventKey === "alt" || eventKey === "opt") return;

        if (event.altKey && lowerCaseKeys.includes(eventKey)) {
          event.preventDefault();
          callback();

          return;
        }
      }

      // Handle single key press
      if (
        lowerCaseKeys.includes(event.key.toLowerCase()) &&
        lowerCaseKeys.length === 1
      ) {
        callback();
      }
    },
    [keys, callback]
  );

  useEffect(() => {
    if (!keys.length) return;

    window.addEventListener("keydown", handleKeyPress, capture);

    return () => {
      window.removeEventListener("keydown", handleKeyPress, capture);
    };
  }, [handleKeyPress, capture, keys]);
};

/**
 * Hook for managing timeouts with automatic cleanup on component unmount.
 *
 * This hook provides a safe way to create timeouts that are automatically
 * cleaned up when the component unmounts, preventing memory leaks and
 * "Can't perform a React state update on an unmounted component" warnings.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const createTimeout = useTimeoutManager();
 *
 *   const handleClick = () => {
 *     createTimeout(() => {
 *       console.log('This will be cleaned up if component unmounts');
 *     }, 1000);
 *   };
 *
 *   return <button onClick={handleClick}>Start Timer</button>;
 * }
 * ```
 */
export const useTimeoutManager = () => {
  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());

  // Helper function to create managed timeouts with cleanup
  const createManagedTimeout = useCallback(
    (callback: () => void, delay: number) => {
      const timeoutId = setTimeout(() => {
        timeoutRefs.current.delete(timeoutId);
        callback();
      }, delay);
      timeoutRefs.current.add(timeoutId);

      return timeoutId;
    },
    []
  );

  // Cleanup all timeouts on unmount
  useEffect(() => {
    const timeouts = timeoutRefs.current;

    return () => {
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  return createManagedTimeout;
};

/**
 * Hook for checking if an element is overflowing. The hook checks for both vertical and horizontal overflow.
 *
 * This hook provides a way to check if an element is overflowing, which can be
 * useful for implementing responsive design patterns such as truncating text
 * with an ellipsis when it overflows.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { ref, isOverflowing, measure } = useIsOverflowing();
 *
 *   return (
 *     <div ref={ref} onMouseEnter={measure}>
 *       {isOverflowing ? '...' : 'Not overflowing'}
 *     </div>
 *   );
 * }
 * ```
 */
export const useIsOverflowing = () => {
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const nodeRef = useRef<HTMLElement | null>(null);

  // Runs when the element mounts, and is handed back for an element that can change size after that — one in a
  // resizable grid column, say.
  const measure = useCallback(() => {
    const node = nodeRef.current;
    if (node) {
      const hasVerticalOverflow = node.scrollHeight > node.clientHeight;
      const hasHorizontalOverflow = node.scrollWidth > node.clientWidth;

      // Check if content is clipped due to line-clamp
      const hasLineClampOverflow = node.offsetHeight < node.scrollHeight;

      setIsOverflowing(
        hasVerticalOverflow || hasHorizontalOverflow || hasLineClampOverflow
      );
    }
  }, []);

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      measure();
    },
    [measure]
  );

  return { ref: setRef, isOverflowing, measure };
};

export const useIsAdmin = (): boolean => {
  const { userInfo } = useConfigContext();

  return userInfo.isAdmin || userInfo.isSuperAdmin;
};

export const useTenantServiceRecordName = (pluralForm: boolean = true) => {
  const { t } = useTranslation();
  const {
    pages: { serviceRecords },
  } = useConfigContext();

  if (serviceRecords?.serviceRecordText) {
    return serviceRecords.serviceRecordText;
  }

  return t("common:event_type.servicerecord", { count: pluralForm ? 3 : 1 });
};

// Whether the calling component is still on screen, for work that outlives it, such as a request answered after the
// user has left. Set again on every mount, since strict mode mounts twice.
export const useIsMounted = () => {
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
};
